import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'
import { useTranslation } from 'react-i18next'
import { upperFirstOnly } from 'utils/format'
import { GradeDropdown } from '../../../performance-report/components/grade-dropdown'
import { StationSelector } from '../../../performance-report/components/station-selector'
import { RegionsDropdown } from '../regions-dropdown'
import { TimeResolutionDropdown } from '../time-resolution-dropdown'
import { PeriodDropdown } from '../period-dropdown'
import { SplitByDropdown } from '../split-by-dropdown'
import { Dropdown } from 'components/common/dropdown'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { showRegionsDropdown } from 'utils/helpers'
import { useAuth } from 'config/context/authContext'
import { captureFilterApplied } from 'config/posthog/events'
import { FilterTypes } from 'config/posthog/constants'
import styles from './Filters.module.scss'
import { useStations } from 'components/common/hooks/useStations'
import { Metrics } from '../metrics'
import { CompetitorAnalysis, KeyPerformanceMetrics, PeriodAnalysis, PortfolioBenchmark, StockAndDeliveries, DisplayTanks, DisplayFuelGrades } from 'utils/constants'
import { parseDrivingDistance } from 'components/station/components/competitor-pricing/components/competitor-map/components/map-markers/utils'
import { ReactComponent as DrivingDistanceIcon } from 'images/competitor-pricing/driving-distance.svg'
import { ReactComponent as StarIcon } from 'images/competitor-pricing/star.svg'

const cx = classNames.bind(styles)

export const ITEMS_LIMIT = 3

const ITEMS_LIMITS_BY_TAB = {
  [PeriodAnalysis]: 3,
  [CompetitorAnalysis]: 1,
  [StockAndDeliveries]: 1,
}

const getItemsLimit = (activeTab) => ITEMS_LIMITS_BY_TAB[activeTab] ?? null

const Filters = ({
  activeTab,
  stationIds,
  setSelectedStation,
  competitorStationIds,
  setSelectedCompetitorStations,
  fuelGradesItems,
  fuelGrades,
  setSelectedGrades,
  isEdgeDataPro,
  competitors,
  competitorsLoading,
  splitBy,
  setSplitBy,
}) => {
  const { t } = useTranslation(['translations'])
  const { showRegionDropdown } = useFlags()
  const { userLocale } = useAuth()
  const { stations } = useStations()

  const divideByItems = [
    { item: DisplayTanks, displayItem: t('widgets.analytics.displayTanks') },
    { item: DisplayFuelGrades, displayItem: t('widgets.analytics.displayFuelGrades') },
  ]

  const gradeFilterLocked = activeTab === StockAndDeliveries && splitBy?.[0] === DisplayFuelGrades

  const handleSplitByChange = splitByValue => {
    setSplitBy(splitByValue)
    captureFilterApplied(FilterTypes.SPLIT_BY)
  }

  return (
    <div className={cx('select-boxes', {
      'portfolio-benchmark': activeTab === PortfolioBenchmark
    })}>
      {activeTab === CompetitorAnalysis ? 
        <Metrics activeTab={activeTab} />
      : null}
      { !isEdgeDataPro &&
        <StationSelector
          stations={stations}
          value={stationIds}
          onValueChange={setSelectedStation}
          placeholder={upperFirstOnly(
            t('widgets.performanceReport.noStationsSelected')
          )}
          allStationsText={upperFirstOnly(
            t('widgets.performanceReport.allStationsSelected')
          )}
          maxHeight="228px"
          selectButtons={activeTab === PortfolioBenchmark || activeTab === KeyPerformanceMetrics}
          itemsLimit={getItemsLimit(activeTab)}
          activeTab={activeTab}
          allowGroups={activeTab === PeriodAnalysis ? true : false}
        />
      }
      {activeTab === CompetitorAnalysis ?
        <StationSelector
          stations={competitors}
          value={competitorStationIds}
          onValueChange={setSelectedCompetitorStations}
          placeholder={upperFirstOnly(
            t('widgets.performanceReport.noCompetitorStationsSelected')
          )}
          allStationsText={upperFirstOnly(
            t('widgets.performanceReport.allCompetitorStationsSelected')
          )}
          maxHeight="228px"
          selectButtons={true}
          itemsLimit={null}
          activeTab={activeTab}
          allowGroups={false}
          filterType={FilterTypes.COMPETITOR_STATION}
          loading={competitorsLoading}
          isHighlighted={competitor => competitor.influencer}
          highlightColour={styles.goldSecondary}
          parseSubHeadingText={competitor => parseDrivingDistance(t, competitor.distances)}
          subHeadingIconLeft={DrivingDistanceIcon}
          subHeadingIconHighlighted={StarIcon}
        /> : null
      }
      {showRegionsDropdown(showRegionDropdown, userLocale) && activeTab === PortfolioBenchmark && <RegionsDropdown />}
      <GradeDropdown
        options={fuelGradesItems}
        value={gradeFilterLocked ? [upperFirstOnly(t('widgets.analytics.allFuelGrades'))] : fuelGrades}
        onValueChange={setSelectedGrades}
        enableIcon={false}
        singular={[CompetitorAnalysis, StockAndDeliveries].includes(activeTab)}
        disabled={gradeFilterLocked}
        dataTestId={'grades-dropdown'}
      />
      <PeriodDropdown activeTab={activeTab} />
      {![CompetitorAnalysis, StockAndDeliveries].includes(activeTab) ?
        <TimeResolutionDropdown activeTab={activeTab} /> : null}
      {activeTab === KeyPerformanceMetrics && (
        <SplitByDropdown
          items={['Station', 'Fuel Grade']}
          value={splitBy}
          onValueChange={handleSplitByChange}
          dataTestId={'split-by-dropdown'}
        />
      )}
      {activeTab === StockAndDeliveries && (
        <Dropdown
          items={divideByItems}
          selectedItem={splitBy?.[0]}
          onValueSelect={(option) => handleSplitByChange([option])}
          enableMultiSelect={false}
          dataTestId={'divide-by-dropdown'}
        />
      )}
    </div>
  )
}

Filters.propTypes = {
  activeTab: PropTypes.string,
  setSelectedStation: PropTypes.func,
  stationIds: PropTypes.arrayOf(PropTypes.string),
  setSelectedCompetitorStations: PropTypes.func,
  competitorStationIds: PropTypes.arrayOf(PropTypes.string),
  fuelGradesItems: PropTypes.arrayOf(PropTypes.string),
  fuelGrades: PropTypes.arrayOf(PropTypes.string),
  setSelectedGrades: PropTypes.func,
  splitBy: PropTypes.arrayOf(PropTypes.string),
  setSplitBy: PropTypes.func,
  isEdgeDataPro: PropTypes.bool,
  competitors: PropTypes.arrayOf(PropTypes.object),
}

Filters.defaultProps = {
  fuelGradesItems: [],
  fuelGrades: [],
  competitors: [],
}

export { Filters }
